import { FileSystemErrorCodes } from "app/modules/remote-control-ng/definitions";
import {
    DosGetDirMessage,
    DosQueryDeviceMessage,
    DosSearchMessage,
    CancelAsyncMessage,
    DosGetFileMessage,
    DosRenameFileMessage,
    DosDeleteFileMessage,
    DosDeleteFolderMessage,
    DosCompressItemMessage,
    DosEmptyFolderMessage,
    DosMakeDirectoryMessage,
    DosSendFileMessage,
    DosSendFileDataMessage
} from "../messages/pocket-com/fileMessages";
import { RcContext } from "./rcContext";
import { RequestReplyService } from "../services/requestReplyService";
import { SubscriptionCleaner } from "../services/subscriptionCleaner";
import { takeUntil } from "rxjs/operators";
import { CallbackType, DataCallbackType, ErrorCallbackType, FileCallbackType, FileDataCallbackType } from "../mcrc-api";
import { Injectable } from "@angular/core";
import { BaseMessage } from "../messages";

enum QueryCmd {
    PCQ_GET_LAST_ERROR = 0,
    PCQ_GET_AGENT_VERSION = 1,
    PCQ_GET_WINDOWS_FOLDER = 2,
    PCQ_GET_AGENT_PATH = 3,
    PCQ_GET_DRIVE_LIST = 4,
    PCQ_GET_IMPERSONATE_USER = 5,
    PCQ_MAX = 6
}

enum CompressionCommandTypes {
    COMPRESS = 0x01,
    DECOMPRESS = 0x02
}

export class FileSystemContext {
    static readonly uploadBlockSize = 1024 * 63; // maximum 1024 bytes blocks allowed by web sockets
    static readonly MAX_PATH = 260; // maximum length of a path in WIN32_FIND_DATA
    static readonly blockSize = 65535;

    public folderCallbackPath: Map<number, string> = new Map<number, string>();
    public fileCallback: FileCallbackType | FileDataCallbackType;
    public fileProgressCallback;
    public fileUploadProgressCallback: (value: number) => void;
    public fileUploadErrorCallback: ErrorCallbackType;
    public fileUploadCompleteCallback: () => void;
    public fileOffset = 0;
    public fileUploadOffset = 0;
    public fileSize = 0;
    public fileArray;

    public filePath: string;
    public fileName: string;
    public uploadFile: File;
    public fileDownloadStop: boolean = false;
    public fileUploadStop: boolean = false;
}

@Injectable()
export class RcFileSystemService extends SubscriptionCleaner {
    static readonly blockSize = 65535;

    private _context: FileSystemContext = RcContext.instance.fileSystem;

    constructor(private requestReplyService: RequestReplyService) {
        super();
    }

    public stopFileDownload = (): void => {
        this._context.fileDownloadStop = true;
    }

    public stopFileUpload = (): void => {
        this._context.fileUploadStop = true;
    }

    public queryDeviceDrives = (callback: DataCallbackType, fallback: CallbackType) => {
        RcContext.instance.session.sendRequest(new DosQueryDeviceMessage(QueryCmd.PCQ_GET_DRIVE_LIST))
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: DosQueryDeviceMessage) => {
                    if (reply.errorCode === 0) {
                        callback(reply.drives);
                    } else {
                        fallback();
                    }
                },
                (error) => fallback()
            )
    }

    public getFolderContents = (path: string, callback: DataCallbackType) => {
        if (callback === undefined) return undefined;
        const message = new DosGetDirMessage(path, false);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: DosGetDirMessage) => callback(reply.response),
            )
        this._context.folderCallbackPath.set(message.reqId, path);
    }

    public searchFolderContents = (searchString: string, path: string, callback: DataCallbackType, fallback: CallbackType): number => {
        if (callback === undefined) return undefined;
        const message = new DosSearchMessage(searchString, path);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: DosSearchMessage) => callback(reply.response),
                (error) => fallback()
            )
        this._context.folderCallbackPath.set(message.reqId, path);
        return message.reqId;
    }

    public cancelSearch = (reqId: number): void => {
        RcContext.instance.session.sendMessage(new CancelAsyncMessage(reqId));
        // Agents are not cancelling searches even though we send the
        // PC_CANCEL_ASYNC message. Because of this, we also discard
        // the callback of the cancelled request, so we discard the response
        // from the agent about the cancelled search
        this.requestReplyService.cancel(reqId);
        this._context.folderCallbackPath.delete(reqId);
    }

    public stopCompression = (reqId: number): void => {
        RcContext.instance.session.sendMessage(new CancelAsyncMessage(reqId));
    }

    /// Rename a file/folder from the device
    /// Original ItemName and NewItemName should be provided
    public renameItem = (origName: string, newName: string, callback: ErrorCallbackType): void => {
        const message = new DosRenameFileMessage(origName, newName, FileSystemContext.blockSize);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => {
                    callback(reply.errorCode)
                },
                (error) => callback(error)
            );
    }

    public getFileContents = (file: string, path: string, callback: FileDataCallbackType, progressCallback: (path: string, completed: number, total: number) => void): void => {
        if (callback === undefined) return null;
        this._context.fileCallback = callback;
        this._context.fileProgressCallback = progressCallback;
        this._context.fileName = file;
        RcContext.instance.session.sendCallbackRequest(new DosGetFileMessage(path, FileSystemContext.blockSize));
    }

    public createDirectory = (newDirectoryPath, callback: ErrorCallbackType | null): void => {
        RcContext.instance.session.sendRequest(new DosMakeDirectoryMessage(newDirectoryPath))
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => {
                    callback(reply.errorCode)
                },
                (error) => callback(error)
            );
    }

    public pause = (): void => {
        setTimeout(() => {
            if (this._context.fileUploadErrorCallback) {
                this._context.fileUploadErrorCallback(FileSystemErrorCodes.RCPauseInterruptedFileAction);
            }
            if (this._context.fileCallback) {
                this._context.fileCallback(undefined, FileSystemErrorCodes.RCPauseInterruptedFileAction);
            }
        }, 500);
    }

    public sendFileInfo = (fileSize: number, filePath: string, callback: ErrorCallbackType): void => {
        this._context.fileUploadOffset = 0;
        const message = new DosSendFileMessage(filePath, fileSize, FileSystemContext.uploadBlockSize);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => callback(reply.errorCode),
                () => callback(FileSystemErrorCodes.FatalError)
            );
    }

    public sendFileChunk = async (errorCode?: number) => {
        if (this._context.fileUploadStop) {
            errorCode = FileSystemErrorCodes.FileUploadStopRequested;
            this._context.fileUploadStop = false;
        }

        // check if error happened uploading file
        if (errorCode > 0) {
            setTimeout(() => {
                this._context.fileUploadErrorCallback(errorCode);
            }, 500);
            return;
        }
        // no error
        this._context.fileUploadProgressCallback(Math.ceil((this._context.fileUploadOffset / this._context.uploadFile.size) * 100));

        // check if file upload was complete
        if (this._context.fileUploadOffset == this._context.uploadFile.size) {
            setTimeout(() => {
                this._context.fileUploadCompleteCallback();
            }, 500);
            return;
        }

        // continue uploading file
        const start = this._context.fileUploadOffset;
        const end = Math.min(start + FileSystemContext.uploadBlockSize, this._context.uploadFile.size);
        this._context.fileUploadOffset = end;
        const blob = this._context.uploadFile.slice(start, end, 'application/octet-stream');
        const fileChunk = await blob.arrayBuffer();

        RcContext.instance.session.sendRequest(new DosSendFileDataMessage(fileChunk))
            .subscribe(
                (reply: BaseMessage) => this.sendFileChunk(reply.errorCode),
                () => this._context.fileUploadErrorCallback(FileSystemErrorCodes.FatalError)
            );
    }

    public sendFileContents = (file: File, fileErrorCallback: ErrorCallbackType, fileProgressCallback: (value: number) => void, fileCompleteCallback: () => void) => {
        this._context.uploadFile = file;
        this._context.fileUploadOffset = 0
        this._context.fileUploadProgressCallback = fileProgressCallback;
        this._context.fileUploadErrorCallback = fileErrorCallback;
        this._context.fileUploadCompleteCallback = fileCompleteCallback;
        this.sendFileChunk();
    }

    /** Deletes a file from the device. Full path to the file should be provided  */
    public deleteFile = (filePath: string, callback: ErrorCallbackType): void => {
        RcContext.instance.session.sendRequest(new DosDeleteFileMessage(filePath))
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => callback(reply.errorCode)
            );
    }

    public deleteFolder = (filePath: string, callback: ErrorCallbackType): void => {
        RcContext.instance.session.sendRequest(new DosDeleteFolderMessage(filePath))
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => callback(reply.errorCode)
            );
    }

    public emptyFolder = (filePath: string, callback: ErrorCallbackType): void => {
        RcContext.instance.session.sendRequest(new DosEmptyFolderMessage(filePath))
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: BaseMessage) => callback(reply.errorCode)
            );
    }

    public compressItem = (filePath: string, callback: FileCallbackType): number => {
        const message = new DosCompressItemMessage(filePath, CompressionCommandTypes.COMPRESS);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: DosCompressItemMessage) => callback(reply.fileName, reply.errorCode),
                (errorCode) => callback(filePath, errorCode),
            );
        return message.reqId;
    }

    public deCompressItem = (filePath, callback: FileCallbackType): number => {
        const message = new DosCompressItemMessage(filePath, CompressionCommandTypes.DECOMPRESS);
        RcContext.instance.session.sendRequest(message)
            .pipe(takeUntil(super.isDestroyed$)).subscribe(
                (reply: DosCompressItemMessage) => callback(reply.fileName, reply.errorCode),
                (errorCode) => callback(filePath, errorCode),
            );
        return message.reqId;
    }
}
